import Axios from "axios";
import { ButtonSecondary } from "components/Atoms/Button";
import { SectionTemplateHeader } from "components/MainSectionTemplate/SectionTemplateHeader";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import SmallDetails from "components/SmallDetails/SmallDetails";
import { useValidateIsNotNull } from "formvalidation/hooks";
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import { FileRejection } from "react-dropzone/typings/react-dropzone";
import { Message, Progress } from "semantic-ui-react";
import { downloadFile } from "utilities/apiUtils";
import { ExtractFileUploadInstruction, SingleFileApi, uploadFileToCoreApi } from "utilities/fileUploadUtils";
import { FileTypes, formatBytes } from "shared/utils/fileutils";
import { FileDropzone } from "./FileDropzone";
import { getAcceptForFileType, getHtmlAcceptForFileType, getIconTypeForFileType, UploadProgress } from "shared/utils/fileTypeUtils";

interface SingleFilePanelProps<
  TApi extends SingleFileApi
> {
  hasRequiredError?: boolean;
  callbackOnChangeInsteadOfUpload?: (file: File) => void;
  title?: string;
  currentValue: JSX.Element | null;
  maxSizeBytes: number;
  fileType: Exclude<FileTypes, "unknown">;
  api: TApi;
  endpoint: string;
  instruction: Omit<ExtractFileUploadInstruction<TApi>, "file">,
  onUploadComplete: (fileId: string, downloadUrl: string) => void;
}

const SingleFilePanel = <TApi extends SingleFileApi>(props: SingleFilePanelProps<TApi>) => {
  const {
    title = RESX.File.resxFile,
    currentValue,
    maxSizeBytes,
    fileType,
    api,
    endpoint,
    instruction,
    onUploadComplete,
  } = props;

  const fileUploadRef = React.useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [progress, setProgress] = useState<UploadProgress>({
    totalSize: 0,
    uploaded: 0,
  });
  const [isDraggingFileOver, setIsDraggingFileOver] = useState(false);
  const displayDropzone = currentValue === null || isDraggingFileOver;

  const onFileUploadInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    onDrop(files);
  }

  const onDrop = async (files: FileList | File[]) => {
    setError(undefined);

    if (files.length !== 1) {
      return;
    }

    if (props.callbackOnChangeInsteadOfUpload) {
      props.callbackOnChangeInsteadOfUpload(files[0]);
      return;
    }

    const onUploadProgress = (e: ProgressEvent<EventTarget>) => {
      setProgress({
        totalSize: e.total,
        uploaded: e.loaded
      });
    };

    setIsUploading(true);
    const res = await uploadFileToCoreApi(
      api,
      endpoint,
      {
        file: files[0],
        ...instruction,
      },
      {
        onUploadProgress,
        fileType,
        timeout: 60000, // 1 minute
      }
    );
    if (res.data.hasErrors) {
      setError(res.data.errors[0].message);
      setIsUploading(false);
      return;
    }
    setIsUploading(false);
    onUploadComplete(res.data.data.fileId, res.data.data.downloadUrl);
  }

  const onDropReject = (fileRejections: FileRejection[]) => {
    const firstRejectionError = fileRejections[0].errors[0].message;
    setError(firstRejectionError);
  }

  const dragEnter = (e: DragEvent) => {
    if (!e.dataTransfer.types.includes("Files")) {
      return;
    }

    setIsDraggingFileOver(true);
  };

  const dragEnd = () => {
    setIsDraggingFileOver(false);
  };

  const dragOver = React.useRef(debounce(dragEnd, 200));

  useEffect(() => {
    document.body.addEventListener("dragenter", dragEnter);
    document.body.addEventListener("dragover", dragOver.current);
    return () => {
      document.body.removeEventListener("dragenter", dragEnter);
      document.body.removeEventListener("dragover", dragOver.current);
    };
  }, []);

  return <SectionPanel.Panel
    key={title}
    header={<SectionTemplateHeader>{title}</SectionTemplateHeader>}
    button={<ButtonSecondary label={RESX.Buttons.Upload} onClick={() => fileUploadRef.current?.click()} />}
  >
    <div className="main-section-template-fields-padding">
      <input
        ref={fileUploadRef}
        onChange={onFileUploadInputChange}
        type="file"
        accept={getHtmlAcceptForFileType(fileType)}
        hidden
      />
      {(currentValue && !isUploading) && <>
        {currentValue}
      </>}
      {(displayDropzone && !isUploading) && <FileDropzone
        error={props.hasRequiredError}
        iconType={getIconTypeForFileType(fileType)}
        accept={getAcceptForFileType(fileType)}
        maxFiles={1}
        maxSizeBytes={maxSizeBytes}
        onDrop={onDrop}
        onDropReject={onDropReject}
      />}
      {isUploading && <div style={{ width: "100%" }}>
        <Progress
          percent={progress.uploaded / progress.totalSize * 100}
          color="green"
          progress
          label={RESX.File.UploadingFiles}
          active={true} />
      </div>}
      {error && <div>
        <Message color="red">{error}</Message>
      </div>}
    </div>
  </SectionPanel.Panel>
}

interface ArtistRiderFilePanelProps {
  title: string;
  artistRiderId: number;
  onUploadComplete: (fileId: string, downloadUrl: string) => void;
}

export const ArtistRiderFilePanel = (props: ArtistRiderFilePanelProps) => {
  const onDownload = async () => {
    const params = {
      itemId: props.artistRiderId,
      fileName: `${props.title}.docx`,
    }

    const fileBlobRes = await Axios.post(
      window.CoreApi.ArtistRider.DownloadFile.absoluteUrl(),
      params,
      {
        responseType: "blob",
        withCredentials: true
      });
    downloadFile(fileBlobRes.data, `${props.title}.docx`);
  }

  return <SingleFilePanel
    fileType="docx"
    maxSizeBytes={serverReference.constants.artistRider.maxAttachedBytes}
    currentValue={<div className="card-floating-bg padding-larger radius">
      <a onClick={onDownload}>{RESX.File.Download}</a>
    </div>}
    api={window.CoreApi.ArtistRider.UploadFile}
    endpoint={window.CoreApi.ArtistRider.UploadFile.absoluteUrl()}
    instruction={{
      itemId: props.artistRiderId,
    }}
    onUploadComplete={props.onUploadComplete}
  />
}

interface ArtistRiderFilePanelNewProps {
  selected: File | null;
  onSelected: (file: File) => void;
}

export const ArtistRiderFilePanelNew = (props: ArtistRiderFilePanelNewProps) => {
  const validation = useValidateIsNotNull({
    key: "artistRiderFile",
    title: RESX.File.resxFile,
    value: props.selected,
  });

  return <SingleFilePanel
    hasRequiredError={validation.error.hasError}
    fileType="docx"
    maxSizeBytes={serverReference.constants.artistRider.maxAttachedBytes}
    currentValue={props.selected ? <div className="card-floating-bg padding-larger radius">
      <SmallDetails
        label={props.selected.name}
        subLabel={formatBytes(props.selected.size)}
      />
    </div> : null}
    api={window.CoreApi.ArtistRider.UploadFile}
    endpoint={window.CoreApi.ArtistRider.UploadFile.absoluteUrl()}
    instruction={{
      itemId: 0,
    }}
    onUploadComplete={() => { }}
    callbackOnChangeInsteadOfUpload={props.onSelected}
  />
}

interface WordTemplateFilePanelProps {
  title: string;
  wordTemplateId: number;
  onUploadComplete: (fileId: string, downloadUrl: string) => void;
}

export const WordTemplateFilePanel = (props: WordTemplateFilePanelProps) => {
  const onDownload = async () => {
    const params = {
      itemId: props.wordTemplateId,
      fileName: `${props.title}.docx`,
    }

    const fileBlobRes = await Axios.post(
      window.CoreApi.WordTemplate.DownloadFile.absoluteUrl(),
      params,
      {
        responseType: "blob",
        withCredentials: true
      });
    downloadFile(fileBlobRes.data, `${props.title}.docx`);
  }

  return <SingleFilePanel
    fileType="docx"
    maxSizeBytes={serverReference.constants.wordTemplate.maxAttachedBytes}
    currentValue={<div className="card-floating-bg padding-larger radius">
      <a onClick={onDownload}>{RESX.File.Download}</a>
    </div>}
    api={window.CoreApi.WordTemplate.UploadFile}
    endpoint={window.CoreApi.WordTemplate.UploadFile.absoluteUrl()}
    instruction={{
      itemId: props.wordTemplateId,
    }}
    onUploadComplete={props.onUploadComplete}
  />
}

interface WordTemplateFilePanelNewProps {
  selected: File | null;
  onSelected: (file: File) => void;
}

export const WordTemplateFilePanelNew = (props: WordTemplateFilePanelNewProps) => {
  const validation = useValidateIsNotNull({
    key: "wordTemplateFile",
    title: RESX.File.resxFile,
    value: props.selected,
  });

  return <SingleFilePanel
    hasRequiredError={validation.error.hasError}
    fileType="docx"
    maxSizeBytes={serverReference.constants.wordTemplate.maxAttachedBytes}
    currentValue={props.selected ? <div className="card-floating-bg padding-larger radius">
      <SmallDetails
        label={props.selected.name}
        subLabel={formatBytes(props.selected.size)}
      />
    </div> : null}
    api={window.CoreApi.WordTemplate.UploadFile}
    endpoint={window.CoreApi.WordTemplate.UploadFile.absoluteUrl()}
    instruction={{
      itemId: 0,
    }}
    onUploadComplete={() => { }}
    callbackOnChangeInsteadOfUpload={props.onSelected}
  />
}

interface WordTemplateForListViewFilePanelProps {
  title: string;
  wordTemplateForListViewId: number;
  onUploadComplete: (fileId: string, downloadUrl: string) => void;
}

export const WordTemplateForListViewFilePanel = (props: WordTemplateForListViewFilePanelProps) => {
  const onDownload = async () => {
    const params = {
      itemId: props.wordTemplateForListViewId,
      fileName: `${props.title}.docx`,
    }

    const fileBlobRes = await Axios.post(
      window.CoreApi.WordTemplateForListView.DownloadFile.absoluteUrl(),
      params,
      {
        responseType: "blob",
        withCredentials: true
      });
    downloadFile(fileBlobRes.data, `${props.title}.docx`);
  }

  return <SingleFilePanel
    fileType="docx"
    maxSizeBytes={serverReference.constants.wordTemplate.maxAttachedBytes}
    currentValue={<div className="card-floating-bg padding-larger radius">
      <a onClick={onDownload}>{RESX.File.Download}</a>
    </div>}
    api={window.CoreApi.WordTemplateForListView.UploadFile}
    endpoint={window.CoreApi.WordTemplateForListView.UploadFile.absoluteUrl()}
    instruction={{
      itemId: props.wordTemplateForListViewId,
    }}
    onUploadComplete={props.onUploadComplete}
  />
}

interface WordTemplateForListViewFilePanelNewProps {
  selected: File | null;
  onSelected: (file: File) => void;
}

export const WordTemplateForListViewFilePanelNew = (props: WordTemplateForListViewFilePanelNewProps) => {
  const validation = useValidateIsNotNull({
    key: "wordTemplateForListViewFile",
    title: RESX.File.resxFile,
    value: props.selected,
  });

  return <SingleFilePanel
    hasRequiredError={validation.error.hasError}
    fileType="docx"
    maxSizeBytes={serverReference.constants.wordTemplate.maxAttachedBytes}
    currentValue={props.selected ? <div className="card-floating-bg padding-larger radius">
      <SmallDetails
        label={props.selected.name}
        subLabel={formatBytes(props.selected.size)}
      />
    </div> : null}
    api={window.CoreApi.WordTemplateForListView.UploadFile}
    endpoint={window.CoreApi.WordTemplateForListView.UploadFile.absoluteUrl()}
    instruction={{
      itemId: 0,
    }}
    onUploadComplete={() => { }}
    callbackOnChangeInsteadOfUpload={props.onSelected}
  />
}